import React, { useState, useEffect, ChangeEvent, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    UniqueIdentifier
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { APIDataMedia } from '@evertel/types'
import { getClosestVersionByHeight, labelFromValue } from '@evertel/utils'
import { Button, Icon, Row, Col, Input, Image } from '@evertel/web/ui'
import { SchemaMediaController } from '../../../controller/medias/SchemaMediaController'

export type Option = { label: string, value: string, image?: number | File }

const OPTION_THUMB_SIZE = 30

// request a small resized version for the thumbnail (2x for retina) instead of the
// full-size default. Falls back to the raw url until versions have been generated
const optionThumbUrl = (media?: APIDataMedia) => {
    if (!media?.url) return undefined
    return getClosestVersionByHeight(OPTION_THUMB_SIZE * 2, media)?.url || media.url
}

interface OptionsProps {
    onChange?: (options: Option[]) => void
    options?: Option[]
    isCheckbox?: boolean
    schemaMediaController: SchemaMediaController
    id?: string
}

const OptionsBuilder: React.FC<OptionsProps> = observer(({
    onChange,
    options,
    isCheckbox,
    schemaMediaController,
    id,
    ...otherProps
}) => {

    const [localOptions, setLocalOptions] = useState<Option[]>([])
    const [localId, setLocalId] = useState('')
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)

    // Sensors for drag detection
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    useEffect(() => {
        //only set this once or when field changes
        if (options && (localOptions?.length === 0 || id !== localId)) {
            setLocalOptions((isCheckbox)
                ? options.map(o => (o.label?.trim()) ? o : { ...o, label: labelFromValue(o.value) })
                : options
            )
            setLocalId(id)
        }
    }, [options])

    // Wrapper function to handle all changes and filter incomplete options before calling onChange
    const handleOnChange = (updatedOptions: Option[]) => {
        setLocalOptions(updatedOptions)

        const isComplete = (o: Option) => !!o.label?.trim() && !!o.value?.trim()

        // Filter out incomplete options (those without both label and value)
        const complete = updatedOptions.filter(isComplete)
        const outbound = ( complete.length === 0 )
            ? [{ label: '', value: '' }]
            : complete

        onChange?.(outbound)
    }

    // Handle input field changes
    const handleFieldChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        const optionsCopy = [...localOptions]

        optionsCopy[index] = {
            ...optionsCopy[index],
            [e.target.name]: e.target.value
        }

        handleOnChange(optionsCopy)
    }

    // Handle blur events to auto-generate and ensure unique values
    const handleFieldBlur = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        const optionsCopy = [...localOptions]
        const currentOption = optionsCopy[index]

        // Determine what value we should use
        let valueToCheck = currentOption.value

        // If value is empty (regardless of which field triggered the blur),
        // try to generate one from the label
        if (!valueToCheck?.length && currentOption.label?.length) {
            valueToCheck = currentOption.label.replace(/\s+/g, '-').toLowerCase()
        }

        // If we have a value to check (either existing or newly generated), ensure it's unique
        if (valueToCheck?.length) {
            const uniqueValue = ensureUniqueValue(valueToCheck, optionsCopy, index)

            // Only update if value changed or was newly generated
            if (uniqueValue !== currentOption.value) {
                optionsCopy[index] = {
                    ...currentOption,
                    value: uniqueValue
                }

                handleOnChange(optionsCopy)
            }
        }
    }

    // Helper function to ensure a value is unique
    const ensureUniqueValue = (baseValue: string, optionItems: Option[], currentIndex: number): string => {
        // Check if the value already exists in another option
        const isDuplicate = optionItems.some((option, idx) =>
            idx !== currentIndex && option.value === baseValue
        )

        if (!isDuplicate) return baseValue

        // If duplicate exists, try adding numeric suffixes until we find a unique value
        let counter = 1
        let newValue = `${baseValue}-${counter}`

        // Keep incrementing counter until we find a value that doesn't exist
        while (optionItems.some((option, idx) =>
            idx !== currentIndex && option.value === newValue
        )) {
            counter++
            newValue = `${baseValue}-${counter}`
        }

        return newValue
    }

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id)
    }, [])

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event

        setActiveId(null)

        if (over && active.id !== over.id) {
            const oldIndex = localOptions.findIndex((_, idx) => `option-${idx}` === active.id)
            const newIndex = localOptions.findIndex((_, idx) => `option-${idx}` === over.id)

            if (oldIndex !== -1 && newIndex !== -1) {
                const reordered = arrayMove(localOptions, oldIndex, newIndex)
                handleOnChange(reordered)
            }
        }
    }, [localOptions, handleOnChange])

    const onAddOptionImage = (index: number, media: File[]) => {
        const image = (Array.isArray(media)) ? media[0] : media
        const { file } = schemaMediaController.addToUploadQueue(image)
        const optionsCopy = [...localOptions]

        optionsCopy[index] = {
            ...optionsCopy[index],
            image: file
        }

        handleOnChange(optionsCopy)
    }

    const addOption = () => {
        const optionsCopy = [...localOptions]
        optionsCopy.push({ label: '', value: '' })
        setLocalOptions(optionsCopy)  // Note: We don't call onChange here since it's an incomplete option
    }

    const deleteOption = (index: number) => {
        const optionsCopy = [...localOptions]
        optionsCopy.splice(index, 1)
        handleOnChange(optionsCopy)
    }

    // Generate sortable IDs for the options
    const sortableIds = localOptions.map((_, idx) => `option-${idx}`)

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={sortableIds}
                strategy={verticalListSortingStrategy}
            >
                <Col
                    id={id}
                    className="options-builder-container pt-2"
                    role="region"
                    {...otherProps}
                >
                    <div className="visually-hidden" id="options-instructions">
                        Drag items to reorder. Each option requires a label and value.
                    </div>

                    {localOptions?.map((option, idx) => {
                        if (!option) return undefined
                        return (
                            <SortableOption
                                key={idx}
                                id={`option-${idx}`}
                                index={idx}
                                option={option}
                                activeId={activeId}
                                isCheckbox={isCheckbox}
                                localOptionsLength={localOptions.length}
                                schemaMediaController={schemaMediaController}
                                onFieldChange={handleFieldChange}
                                onFieldBlur={handleFieldBlur}
                                onAddOptionImage={onAddOptionImage}
                                onDeleteOption={deleteOption}
                                otherProps={otherProps}
                            />
                        )
                    })}

                    <div>
                        <Button
                            color="link"
                            size="sm"
                            onClick={addOption}
                            className="me-4 float-right"
                            aria-label="Add new option"
                        >
                            Add option
                        </Button>
                    </div>
                </Col>
            </SortableContext>
        </DndContext>
    )
})

interface SortableOptionProps {
    id: string
    index: number
    option: Option
    activeId: UniqueIdentifier | null
    isCheckbox?: boolean
    localOptionsLength: number
    schemaMediaController: SchemaMediaController
    onFieldChange: (index: number, e: ChangeEvent<HTMLInputElement>) => void
    onFieldBlur: (index: number, e: ChangeEvent<HTMLInputElement>) => void
    onAddOptionImage: (index: number, media: File[]) => void
    onDeleteOption: (index: number) => void
    otherProps?: Record<string, unknown>
}

const SortableOption: React.FC<SortableOptionProps> = ({
    id,
    index,
    option,
    activeId,
    isCheckbox,
    localOptionsLength,
    schemaMediaController,
    onFieldChange,
    onFieldBlur,
    onAddOptionImage,
    onDeleteOption,
    otherProps
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id })

    // Only apply transform/transition during active drag
    // This prevents the jump-back-then-animate issue when drag ends
    const style = {
        transform: activeId ? CSS.Transform.toString(transform) : undefined,
        transition: activeId ? transition : undefined,
        opacity: isDragging ? 0.5 : 1
    }

    return (
        <Row
            ref={setNodeRef}
            style={style}
            valign="center"
            className="mb-2"
            role="group"
            aria-label={`Option ${index + 1}`}
        >
            {/* DRAG HANDLE */}
            <div
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                tabIndex={-1}
                role="button"
                aria-label={`Drag to reorder option ${index + 1}`}
                aria-describedby="options-instructions"
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            >
                <Icon
                    name="grip-dots-vertical"
                    className="me-2"
                    aria-hidden="true"
                />
            </div>

            {isCheckbox && (
                <Button
                    color="gray300"
                    ghost
                    className="p-1 me-2"
                    upload={true}
                    onUpload={(file) => onAddOptionImage(index, file)}
                    fileTypes="image/*"
                    title="Select image"
                    aria-label={`Select image for option ${index + 1}`}
                >
                    {option?.image ? (
                        <Image
                            url={
                                option.image instanceof File
                                    ? schemaMediaController.getLocalFileURL(option.image as File) as string
                                    : optionThumbUrl(schemaMediaController.mediaArrays?.uploaded?.find(m => m.id === option.image)) as string
                            }
                            resizeMode="contain"
                            width={OPTION_THUMB_SIZE}
                            height={OPTION_THUMB_SIZE}
                            alt={`Image for ${option.label || `option ${index + 1}`}`}
                        />
                    ) : (
                        <Icon
                            name="image"
                            color="body-color"
                            aria-hidden="true"
                        />
                    )}
                </Button>
            )}

            <Input
                name="label"
                value={option.label}
                placeholder="Label..."
                className="me-2"
                onChange={(event) => onFieldChange(index, event)}
                onBlur={(event) => onFieldBlur(index, event)}
                aria-label={`Label for option ${index + 1}`}
                {...otherProps}
            />
            <Input
                name="value"
                value={option.value}
                placeholder={isCheckbox ? 'Value when checked...' : 'Value...'}
                className={localOptionsLength === 1 ? 'me-4' : ''}
                onChange={(event) => onFieldChange(index, event)}
                onBlur={(event) => onFieldBlur(index, event)}
                aria-label={`Value for option ${index + 1}`}
                {...otherProps}
            />

            {localOptionsLength > 1 && (
                <button
                    className="options-builder-delete-option"
                    onClick={() => onDeleteOption(index)}
                    title="Delete Option"
                    aria-label={`Delete option ${index + 1}`}
                >
                    <Icon
                        name="circle-xmark"
                        type="solid"
                        color="gray300"
                        aria-hidden="true"
                    />
                </button>
            )}
        </Row>
    )
}

export default OptionsBuilder
OptionsBuilder.displayName = 'OptionsBuilder'
